import React, { useState, useEffect, useCallback } from "react";

//import localization
import { useTranslation } from "react-i18next";

import dayjs from "dayjs";

import DatePicker from "../utils/DatePicker";

import { Grid, Row, Col, Form, Select, Divider, Input, Button } from "antd";
import { MinusCircleOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";

import http from "../utils/http";

import { DatePatterns, durationLimit } from "../utils/consts";
import GeneralErrorMsg from "../utils/GeneralErrorMsg";

import { stateContext } from "./MainView";

const { useBreakpoint } = Grid;

const { RangePicker } = DatePicker;
const { Option } = Select;

function TripDetailsForm({ metaValues, query, setMetaValues, localeAntD }) {
	//Locale Hooks
	const { t } = useTranslation();

	//formState: from stateContext
	const [formState, setFormState] = React.useContext(stateContext);
	//form: Form Initialization
	const [form] = Form.useForm();
	//selectedProduct: state that holds the current selectedProduct
	//Used: in order to control the state of the PlanGroup
	const [selectedProduct, setSelectedProduct] = useState({});
	const [selectedPlan, setSelectedPlan] = useState({ AmountRequired: false, MaxDurationDays: durationLimit });
	//loadingState: Used for the loading indication on the Get Plans button
	const [loadingState, setLoadingState] = useState(false);

	//dates: holds the current selected time range
	const [dates, setDates] = useState([]);
	//hackValue: is used as hack to clear the Range Picker. Otherwise it causes an error
	const [hackValue, setHackValue] = useState();
	//currentSelectedRange: holds the two ends of the RangePicker
	const [currentSelectedRange, setCurrentSelectedRange] = useState();

	//Controller for window resizing.
	//Used in: Form
	const screen = useBreakpoint();

	useEffect(() => {
		setHackValue([]);
		setDates([]);
		setCurrentSelectedRange();
	}, [selectedPlan]);

	//On change clear the PlanGroup field
	const onChangeProduct = useCallback(
		(value) => {
			form.setFieldsValue({
				PlanGroup: ""
			});
			setSelectedProduct(JSON.parse(value));
		},
		[form]
	);

	//On change clear the PlanGroup field
	const onChangePlan = useCallback((value) => {
		setSelectedPlan(JSON.parse(value));
	}, []);

	//Function that disables certain dates according to specific conditions
	//By default disable all days before current - current + 6 months
	//On select allow maximum range of 30 days between start-end of duration
	const disabledRange = useCallback(
		(current) => {
			let timeRangeStart = null;
			let timeRangeEnd = null;

			//If no selection has been taken then disable all days before current

			if (!dates || (dates.length === 0 && !currentSelectedRange)) {
				timeRangeStart = current && current < dayjs().endOf("day");
				timeRangeEnd = current && current > dayjs().add(5, "month").endOf("day");
			} else {
				//Disable dates that are out of range of 30 days
				timeRangeStart = dates[1] && dates[1].diff(current, "days") >= selectedPlan.MaxDurationDays;
				timeRangeEnd = dates[0] && current.diff(dates[0], "days") >= selectedPlan.MaxDurationDays;
				if (currentSelectedRange) {
					timeRangeStart = currentSelectedRange[1] && currentSelectedRange[1].diff(current, "days") >= selectedPlan.MaxDurationDays;
					timeRangeEnd = currentSelectedRange[0] && current.diff(currentSelectedRange[0], "days") >= selectedPlan.MaxDurationDays;
				}
			}
			return timeRangeStart || timeRangeEnd;
		},
		[dates, currentSelectedRange, selectedPlan]
	);

	//Function that allows the above disabledRange function to work correctly
	const onRangePickerChange = (open) => {
		if (open) {
			setHackValue([]);
			setDates([]);
		} else {
			setHackValue(undefined);
		}
	};

	//Function that prevents the user from selecting dates after the current day
	const disabledBirthDate = useCallback((current) => {
		return current && current > dayjs().endOf("day");
	}, []);

	const onMoneyChange = useCallback(
		(value, name) => {
			const reg = /^-?\d*(\.\d*)?$/;
			const fields = form.getFieldsValue();

			const { Travellers } = fields;

			if ((!isNaN(value) && reg.test(value)) || value === "") {
				Object.assign(Travellers[name], { Money: value });
				form.setFieldsValue({ Travellers });
			} else {
				Object.assign(Travellers[name], { Money: "" });
				form.setFieldsValue({ Travellers });
			}
		},
		[form]
	);

	//Function that resets state of button if Form fails to finish
	const onFinishFailed = () => {
		//Set button's loading state to false
		setLoadingState(false);
	};

	//Function that sends the filled form to /plans get in response new metaValues for Plans
	const onFinish = (values) => {
		http
			.post(`/travelinsurancesingle/plans?ch=${query.Channel}&lg=${query.Language}`, {
				ProductId: JSON.parse(values.Product).ID,
				PlanGroupId: JSON.parse(values.PlanGroup).ID,
				Start: values.Duration[0].format(),
				End: values.Duration[1].format(),
				ZoneId: JSON.parse(values.Destination).ZoneId,
				DestinationId: JSON.parse(values.Destination).ID,
				DepartureId: JSON.parse(values.Departure).ID,
				Items: values.Travellers.map((item) => {
					return { Birth: item.BirthDate.format(), Money: item.Money };
				})
			})
			.then((response) => {
				//Update meta values with Plans
				setMetaValues((value) => {
					return { ...value, Plans: response.data };
				});

				//Set button's loading state to false
				setLoadingState(false);

				// Update form State
				setFormState((formState) => {
					return {
						...formState,
						CurrentStep: formState.CurrentStep + 1,
						ProductForm: {
							Product: JSON.parse(values.Product),
							PlanGroup: JSON.parse(values.PlanGroup),
							Departure: JSON.parse(values.Departure),
							Destination: JSON.parse(values.Destination),
							Duration: values.Duration,
							Travellers: values.Travellers
						}
					};
				});
			})
			.catch((error) => {
				console.log(error);
				GeneralErrorMsg({ title: t("ErrorMessageTitle"), content: t("ErrorMessageContent") });

				//Set button's loading state to false
				setLoadingState(false);
			});
	};

	//In case the user returns to the JourneyForm the useEffect will check if there are values inside the formState
	useEffect(() => {
		if (formState?.ProductForm?.Product) {
			setSelectedProduct(formState.ProductForm.Product);
		}
		window.scrollTo(0, 0);
	}, [formState.ProductForm.Product]);

	return (
		<>
			<Form
				style={{ padding: 24 }}
				form={form}
				name="planOffer"
				requiredMark={false}
				initialValues={
					formState?.ProductForm?.Product
						? {
								Product: JSON.stringify(formState.ProductForm.Product),
								PlanGroup: JSON.stringify(formState.ProductForm.PlanGroup),
								Departure: JSON.stringify(formState.ProductForm.Departure),
								Destination: JSON.stringify(formState.ProductForm.Destination),
								Duration: formState.ProductForm.Duration,
								Travellers: formState.ProductForm.Travellers
						  }
						: {
								Departure: JSON.stringify(metaValues?.Departures.find((item) => item.ID === "12")),
								Destination: JSON.stringify(metaValues?.Destinations[0]),
								Travellers: [{ Money: null }]
						  }
				}
				onFinishFailed={onFinishFailed}
				onFinish={onFinish}
			>
				<Row gutter={12}>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item
							name="Product"
							label={t("TypeOfTrip")}
							labelCol={{ span: 24 }}
							rules={[
								{
									required: true,
									message: t("MissingInformation")
								}
							]}
						>
							<Select onChange={onChangeProduct}>
								{metaValues?.Products?.map((selection) => (
									<Option key={selection.ID} value={JSON.stringify(selection)}>
										{selection.Name}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item
							name="PlanGroup"
							label={t("InsurancePlan")}
							labelCol={{ span: 24 }}
							rules={[
								{
									required: true,
									message: t("MissingInformation")
								}
							]}
						>
							<Select disabled={!selectedProduct.PlanGroups} onChange={onChangePlan}>
								{selectedProduct?.PlanGroups?.map((selection) => (
									<Option key={selection.ID} value={JSON.stringify(selection)}>
										{selection.Name}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
				</Row>
				<Row>
					<Col span={24}>
						<Form.Item
							name="Duration"
							label={t("TripDuration")}
							labelCol={{ span: 24 }}
							rules={[
								{
									type: "array",
									required: true,
									message: t("MissingInformation")
								}
							]}
						>
							<RangePicker
								disabled={!selectedPlan?.ID}
								style={{ width: "100%" }}
								locale={localeAntD.DatePicker}
								format={DatePatterns.LongDate}
								disabledDate={disabledRange}
								value={hackValue || currentSelectedRange}
								onCalendarChange={(val) => setDates(val)}
								onChange={(val) => setCurrentSelectedRange(val)}
								onOpenChange={onRangePickerChange}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Row gutter={12}>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item
							name="Departure"
							label={t("Departure")}
							labelCol={{ span: 24 }}
							rules={[
								{
									required: true,
									message: t("MissingInformation")
								}
							]}
						>
							<Select showSearch={true} optionFilterProp="children">
								{metaValues?.Departures?.map((selection) => (
									<Option key={selection.ID} value={JSON.stringify(selection)}>
										{selection.Name}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
					<Col xs={24} sm={12} lg={12}>
						<Form.Item
							name="Destination"
							label={t("YourDestination")}
							labelCol={{ span: 24 }}
							rules={[
								{
									required: true,
									message: t("MissingInformation")
								}
							]}
						>
							<Select>
								{metaValues?.Destinations?.map((selection) => (
									<Option key={selection.ID} value={JSON.stringify(selection)}>
										{selection.Name}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Col>
				</Row>

				<Form.List
					name="Travellers"
					rules={[
						{
							validator: async (_, names) => {
								if (!names || names.length < 1) {
									return Promise.reject(new Error("Add at least 1 traveller."));
								}
							}
						}
					]}
				>
					{(fields, { add, remove }, { errors }) => (
						<>
							<p style={{ textAlign: "center" }}>{t("AddTheNumberOfTravellersYourPolicyNeedsToCover")}</p>
							<Row justify="center" gutter={12}>
								<Col>
									<Form.Item>
										<Button type="primary" onClick={() => remove(fields.length - 1)} disabled={fields.length < 2 ? true : false} icon={<MinusOutlined />} />
										<Form.ErrorList errors={errors} />
									</Form.Item>
								</Col>
								<Col span={2}>
									<div style={{ textAlign: "center", marginTop: 5 }}>{fields.length}</div>
								</Col>
								<Col>
									<Form.Item>
										<Button type="primary" onClick={() => add()} disabled={fields.length > 9 ? true : false} icon={<PlusOutlined />} />
										<Form.ErrorList errors={errors} />
									</Form.Item>
								</Col>
							</Row>
							{fields.map(({ key, name, fieldKey, ...restField }) => {
								//key: holds the current key created which is incremental
								//name: holds the index of each form item
								//restField: holds isListField object which is required for each Form item
								return (
									<div key={`traveller${key}`}>
										<Divider plain style={{ margin: "12px 0" }}>
											{name + 1}# {t("Traveller")}
										</Divider>
										<Row gutter={12} key={key}>
											<Col xs={24} sm={12} lg={12}>
												<Form.Item
													{...restField}
													name={[name, "BirthDate"]}
													fieldKey={[fieldKey, "BirthDate"]}
													rules={[
														{ required: true, message: "" },
														({ getFieldValue }) => ({
															validator(_, value) {
																if (getFieldValue("Duration")) {
																	if (!value || getFieldValue("Duration")[0].diff(value, "day") > 30) {
																		return Promise.resolve();
																	} else return Promise.reject(new Error(t("MustBeAtLeastOneMonthOldBeforeTheTrip")));
																} else return Promise.reject(new Error(t("PleaseFillTripDuration")));
															}
														})
													]}
												>
													<DatePicker locale={localeAntD.DatePicker} placeholder={t("BirthDate")} format={DatePatterns.LongDate} showToday={false} disabled={!form.getFieldValue("Duration")} disabledDate={disabledBirthDate} style={{ width: "100%" }} />
												</Form.Item>
											</Col>
											<Col xs={24} sm={fields.length > 1 ? 10 : 12} lg={fields.length > 1 ? 10 : 12}>
												<Form.Item {...restField} name={[name, "Money"]} fieldKey={[fieldKey, "Money"]} rules={[{ required: selectedPlan.AmountRequired, message: "" }]}>
													{/* <InputNumber placeholder={t("Cost")} min={0} formatter={(value) => (value ? `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "")} parser={(value) => value.replace(/€\s?|(,*)/g, "")} style={{ width: "100%" }} /> */}
													<Input disabled={!selectedPlan.AmountRequired} suffix="€" placeholder={t("TripCostPerTraveller")} onChange={(e) => onMoneyChange(e.target.value, name)} maxLength={10} />
												</Form.Item>
											</Col>
											{fields.length > 1 ? (
												<Col xs={24} lg={2}>
													<Form.Item>
														{screen.xs === true || (screen.sm === true && screen.lg === false) ? (
															<Button type="primary" block onClick={() => remove(name)}>
																{t("Delete")}
															</Button>
														) : (
															<MinusCircleOutlined className="dynamic-delete-button" onClick={() => remove(name)} />
														)}
													</Form.Item>
												</Col>
											) : null}
										</Row>
									</div>
								);
							})}
						</>
					)}
				</Form.List>

				<Button type="primary" htmlType="submit" block loading={loadingState} onClick={() => setLoadingState(true)}>
					{t("GetQuote")}
				</Button>
			</Form>
		</>
	);
}

export default React.memo(TripDetailsForm);
